import { getCourses, orEmptyArray } from '@lms/api-client/backend';
import { LoaderCircle } from 'lucide-react';
import useSWR from 'swr';
import { useTranslations } from 'use-intl';
import {
  PageBox,
  PageButtons,
  PageHeader,
  PageTitle,
} from '@/components/admin/page-box';
import { useSiteAccess } from '@/features/auth/use-auth';
import { useBrand } from '@/features/branding/brand-provider';
import { AddCourseButton } from '@/features/courses/components/add-course-button';
import { CourseCard } from '@/features/courses/components/course-card';
import { EmptyList } from '@/features/courses/components/empty-list';

// Ports app/[brand]/admin/courses/page.tsx. The Next server component awaited
// the courses and current user; here they load via SWR and the super-admin gate
// comes from `useSiteAccess`. Adding a course is super-admin only, as in Next.
export function Courses() {
  const t = useTranslations('addcourse');
  const { brand: site, slug } = useBrand();
  const { access } = useSiteAccess(slug);
  const isSuperAdmin = access?.superAdmin ?? false;

  const { data: courses, isLoading } = useSWR(['admin/courses', site.id], () =>
    orEmptyArray(getCourses(site.id)),
  );

  if (isLoading || courses === undefined) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoaderCircle className="text-primary size-8 animate-spin" />
      </div>
    );
  }

  return (
    <PageBox>
      <PageHeader>
        <PageTitle>{t('courses')}</PageTitle>
        <PageButtons>
          {isSuperAdmin && <AddCourseButton locationId={site.id} slug={slug} />}
        </PageButtons>
      </PageHeader>
      <div className="flex flex-row flex-wrap items-start justify-start gap-8">
        {courses.map((course) => (
          <CourseCard
            key={course.id}
            course={course}
            slug={slug}
            isSuperAdmin={isSuperAdmin}
          />
        ))}
      </div>
      {courses.length === 0 && <EmptyList />}
    </PageBox>
  );
}
